/**
 * @module useAirport
 */
import { useCallback } from 'react'
import {
  useGetAirportsMutation,
  useGetAirportByIdMutation,
  useGetAirportByCodeMutation,
  useCreateAirportMutation,
  useUpdateAirportMutation,
  useDeleteAirportMutation,
  useUpdateAirportStatusMutation,
  useAddRunwayMutation,
  useUpdateRunwayMutation,
  useRemoveRunwayMutation,
  useSearchAirportsByCityMutation,
  useSearchAirportsByCountryMutation,
  useGetAirportsByTypeMutation,
  useGetNearbyAirportsMutation,
} from '../services/airport.service'

export const useAirport = () => {
  // Basic CRUD operations
  const [executeGetAirports, { isLoading: isGetAirportsLoading }] = useGetAirportsMutation()
  const [executeGetAirportById, { isLoading: isGetAirportByIdLoading }] =
    useGetAirportByIdMutation()
  const [executeGetAirportByCode, { isLoading: isGetAirportByCodeLoading }] =
    useGetAirportByCodeMutation()
  const [executeCreateAirport, { isLoading: isCreateAirportLoading }] = useCreateAirportMutation()
  const [executeUpdateAirport, { isLoading: isUpdateAirportLoading }] = useUpdateAirportMutation()
  const [executeDeleteAirport, { isLoading: isDeleteAirportLoading }] = useDeleteAirportMutation()

  // Airport-specific operations
  const [executeUpdateAirportStatus, { isLoading: isUpdateAirportStatusLoading }] =
    useUpdateAirportStatusMutation()
  const [executeAddRunway, { isLoading: isAddRunwayLoading }] = useAddRunwayMutation()
  const [executeUpdateRunway, { isLoading: isUpdateRunwayLoading }] = useUpdateRunwayMutation()
  const [executeRemoveRunway, { isLoading: isRemoveRunwayLoading }] = useRemoveRunwayMutation()

  // Search and filter operations
  const [executeSearchAirportsByCity, { isLoading: isSearchAirportsByCityLoading }] =
    useSearchAirportsByCityMutation()
  const [executeSearchAirportsByCountry, { isLoading: isSearchAirportsByCountryLoading }] =
    useSearchAirportsByCountryMutation()
  const [executeGetAirportsByType, { isLoading: isGetAirportsByTypeLoading }] =
    useGetAirportsByTypeMutation()
  const [executeGetNearbyAirports, { isLoading: isGetNearbyAirportsLoading }] =
    useGetNearbyAirportsMutation()

  // Basic CRUD async functions
  const getAirportsAsync = useCallback(async () => {
    if (isGetAirportsLoading) {
      return
    }
    try {
      const response = await executeGetAirports().unwrap()
      return response.data.airports
    } catch (error) {
      console.error('#getAirportsAsync: Error fetching airports', error)
      throw error
    }
  }, [executeGetAirports, isGetAirportsLoading])

  const getAirportByIdAsync = useCallback(
    async (airportId) => {
      if (isGetAirportByIdLoading) {
        return
      }
      try {
        const response = await executeGetAirportById(airportId).unwrap()
        return response.data.airport
      } catch (error) {
        console.error('#getAirportByIdAsync: Error fetching airport by ID', error)
        throw error
      }
    },
    [executeGetAirportById, isGetAirportByIdLoading],
  )

  const getAirportByCodeAsync = useCallback(
    async (code) => {
      if (isGetAirportByCodeLoading) {
        return
      }
      try {
        const response = await executeGetAirportByCode(code).unwrap()
        return response.data.airport
      } catch (error) {
        console.error('#getAirportByCodeAsync: Error fetching airport by code', error)
        throw error
      }
    },
    [executeGetAirportByCode, isGetAirportByCodeLoading],
  )

  const createAirportAsync = useCallback(
    async (airport) => {
      if (isCreateAirportLoading) {
        return
      }
      try {
        const response = await executeCreateAirport(airport).unwrap()
        return response.data.airport
      } catch (error) {
        console.error('#createAirportAsync: Error creating airport', error)
        throw error
      }
    },
    [executeCreateAirport, isCreateAirportLoading],
  )

  const updateAirportAsync = useCallback(
    async (airportId, updates) => {
      if (isUpdateAirportLoading) {
        return
      }
      try {
        const response = await executeUpdateAirport({ airportId, ...updates }).unwrap()
        return response.data.airport
      } catch (error) {
        console.error('#updateAirportAsync: Error updating airport', error)
        throw error
      }
    },
    [executeUpdateAirport, isUpdateAirportLoading],
  )

  const deleteAirportAsync = useCallback(
    async (airportId) => {
      if (isDeleteAirportLoading) {
        return
      }
      try {
        const response = await executeDeleteAirport(airportId).unwrap()
        return response.data
      } catch (error) {
        console.error('#deleteAirportAsync: Error deleting airport', error)
        throw error
      }
    },
    [executeDeleteAirport, isDeleteAirportLoading],
  )

  // Airport-specific async functions
  const updateAirportStatusAsync = useCallback(
    async (airportId, status) => {
      if (isUpdateAirportStatusLoading) {
        return
      }
      try {
        const response = await executeUpdateAirportStatus({ airportId, status }).unwrap()
        return response.data.airport
      } catch (error) {
        console.error('#updateAirportStatusAsync: Error updating airport status', error)
        throw error
      }
    },
    [executeUpdateAirportStatus, isUpdateAirportStatusLoading],
  )

  const addRunwayAsync = useCallback(
    async (airportId, runway) => {
      if (isAddRunwayLoading) {
        return
      }
      try {
        const response = await executeAddRunway({ airportId, runway }).unwrap()
        return response.data.airport
      } catch (error) {
        console.error('#addRunwayAsync: Error adding runway', error)
        throw error
      }
    },
    [executeAddRunway, isAddRunwayLoading],
  )

  const updateRunwayAsync = useCallback(
    async (airportId, runwayNumber, updates) => {
      if (isUpdateRunwayLoading) {
        return
      }
      try {
        const response = await executeUpdateRunway({ airportId, runwayNumber, updates }).unwrap()
        return response.data.airport
      } catch (error) {
        console.error('#updateRunwayAsync: Error updating runway', error)
        throw error
      }
    },
    [executeUpdateRunway, isUpdateRunwayLoading],
  )

  const removeRunwayAsync = useCallback(
    async (airportId, runwayNumber) => {
      if (isRemoveRunwayLoading) {
        return
      }
      try {
        const response = await executeRemoveRunway({ airportId, runwayNumber }).unwrap()
        return response.data.airport
      } catch (error) {
        console.error('#removeRunwayAsync: Error removing runway', error)
        throw error
      }
    },
    [executeRemoveRunway, isRemoveRunwayLoading],
  )

  // Search and filter async functions
  const searchAirportsByCityAsync = useCallback(
    async (city) => {
      if (isSearchAirportsByCityLoading) {
        return
      }
      try {
        const response = await executeSearchAirportsByCity(city).unwrap()
        return response.data.airports
      } catch (error) {
        console.error('#searchAirportsByCityAsync: Error searching airports by city', error)
        throw error
      }
    },
    [executeSearchAirportsByCity, isSearchAirportsByCityLoading],
  )

  const searchAirportsByCountryAsync = useCallback(
    async (country) => {
      if (isSearchAirportsByCountryLoading) {
        return
      }
      try {
        const response = await executeSearchAirportsByCountry(country).unwrap()
        return response.data.airports
      } catch (error) {
        console.error('#searchAirportsByCountryAsync: Error searching airports by country', error)
        throw error
      }
    },
    [executeSearchAirportsByCountry, isSearchAirportsByCountryLoading],
  )

  const getAirportsByTypeAsync = useCallback(
    async (type) => {
      if (isGetAirportsByTypeLoading) {
        return
      }
      try {
        const response = await executeGetAirportsByType(type).unwrap()
        return response.data.airports
      } catch (error) {
        console.error('#getAirportsByTypeAsync: Error fetching airports by type', error)
        throw error
      }
    },
    [executeGetAirportsByType, isGetAirportsByTypeLoading],
  )

  const getNearbyAirportsAsync = useCallback(
    async (params) => {
      if (isGetNearbyAirportsLoading) {
        return
      }
      try {
        const response = await executeGetNearbyAirports(params).unwrap()
        return response.data.airports
      } catch (error) {
        console.error('#getNearbyAirportsAsync: Error fetching nearby airports', error)
        throw error
      }
    },
    [executeGetNearbyAirports, isGetNearbyAirportsLoading],
  )

  return {
    // Basic CRUD functions
    getAirportsAsync,
    getAirportByIdAsync,
    getAirportByCodeAsync,
    createAirportAsync,
    updateAirportAsync,
    deleteAirportAsync,

    // Airport-specific functions
    updateAirportStatusAsync,
    addRunwayAsync,
    updateRunwayAsync,
    removeRunwayAsync,

    // Search and filter functions
    searchAirportsByCityAsync,
    searchAirportsByCountryAsync,
    getAirportsByTypeAsync,
    getNearbyAirportsAsync,

    // Loading states
    isGetAirportsLoading,
    isGetAirportByIdLoading,
    isGetAirportByCodeLoading,
    isCreateAirportLoading,
    isUpdateAirportLoading,
    isDeleteAirportLoading,
    isUpdateAirportStatusLoading,
    isAddRunwayLoading,
    isUpdateRunwayLoading,
    isRemoveRunwayLoading,
    isSearchAirportsByCityLoading,
    isSearchAirportsByCountryLoading,
    isGetAirportsByTypeLoading,
    isGetNearbyAirportsLoading,
  }
}
